import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ClinicalFields from "@/components/admin-clients/ClinicalFields";
import ClientFactSheet from "@/components/admin-clients/ClientFactSheet";
import ClientStrengthsNeedsTab from "@/components/admin-clients/ClientStrengthsNeedsTab";
import ClientRiskAssessmentTab from "@/components/admin-clients/ClientRiskAssessmentTab";
import ClientInfoTab from "@/components/admin-clients/ClientInfoTab";
import ClientConsentsTab from "@/components/admin-clients/ClientConsentsTab";
import { Plus, Loader2, X, Mic } from "lucide-react";
import { toast } from "sonner";
import TagInput from "@/components/TagInput";
import ClientCard from "@/components/admin-clients/ClientCard";
import ClientFamilyInvites from "@/components/admin-clients/ClientFamilyInvites";
import ClientMedications from "@/components/admin-clients/ClientMedications";
import AdminVoiceIntakeModal from "@/components/AdminVoiceIntakeModal";
import AdminVoiceFillModal from "@/components/AdminVoiceFillModal";
import { Sparkles } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const blank = {
  name: "",
  address: "",
  dob: "",
  gender: "",
  pronouns: "",
  primary_diagnosis: "",
  allergies: [],
  diet: "",
  diet_notes: "",
  mobility: "",
  cognition: "",
  fall_risk: "",
  code_status: "",
  advance_directive_on_file: false,
  notes: "",
  assigned_caregiver_ids: [],
  scheduled_days: [],
  meds_locked: false,
  doctor_name: "",
  doctor_phone: "",
  family_contact_name: "",
  family_contact_phone: "",
  emergency_note: "",
  billing_rate: "",
  billing_notes: "",
  required_skills: [],
  preferred_skills: [],
  required_languages: [],
  latitude: "",
  longitude: "",
  geofence_radius_m: "",
  medicaid_member_id: "",
  payer_id: "",
};

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | client object
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  // 'factsheet' (default for existing clients) | 'edit' (form). New-client always starts on edit.
  const [activeTab, setActiveTab] = useState("factsheet");

  // Sub-section state — owned here so re-opening the modal hydrates cleanly
  const [invites, setInvites] = useState([]);
  const [medications, setMedications] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [voiceIntakeOpen, setVoiceIntakeOpen] = useState(false);
  const [voiceCarePlanOpen, setVoiceCarePlanOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const [c, cg] = await Promise.all([
      api.get("/clients"),
      api.get("/auth/caregivers"),
    ]);
    setClients(c.data);
    setCaregivers(cg.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Deep-link support — `?client=<id>` opens the edit modal for that client.
  // Used by AdminIncidents → "Open client record" and any other internal link.
  useEffect(() => {
    const cid = searchParams.get("client");
    if (!cid || editing) return;
    const target = clients.find((c) => c.id === cid);
    if (target) {
      startEdit(target);
      // Honour `?tab=` deep-links from Admin Dashboard / Incidents widgets
      const t = searchParams.get("tab");
      if (t && ["factsheet", "edit", "strengths", "risk", "info", "consents"].includes(t)) {
        setActiveTab(t);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, searchParams]);

  const startNew = () => {
    setForm(blank);
    setEditing("new");
    setInvites([]);
    setMedications([]);
    setActiveTab("edit");  // new clients have no fact sheet yet
  };

  const startEdit = async (c) => {
    setForm({
      name: c.name,
      address: c.address || "",
      dob: c.dob || "",
      notes: c.notes || "",
      // Fact-sheet identity + clinical baseline
      gender: c.gender || "",
      pronouns: c.pronouns || "",
      primary_diagnosis: c.primary_diagnosis || "",
      allergies: c.allergies || [],
      diet: c.diet || "",
      diet_notes: c.diet_notes || "",
      mobility: c.mobility || "",
      cognition: c.cognition || "",
      fall_risk: c.fall_risk || "",
      code_status: c.code_status || "",
      advance_directive_on_file: !!c.advance_directive_on_file,
      assigned_caregiver_ids: c.assigned_caregiver_ids || [],
      scheduled_days: c.scheduled_days || [],
      meds_locked: !!c.meds_locked,
      doctor_name: c.doctor_name || "",
      doctor_phone: c.doctor_phone || "",
      family_contact_name: c.family_contact_name || "",
      family_contact_phone: c.family_contact_phone || "",
      emergency_note: c.emergency_note || "",
      billing_rate: c.billing_rate == null ? "" : String(c.billing_rate),
      billing_notes: c.billing_notes || "",
      required_skills: c.required_skills || [],
      preferred_skills: c.preferred_skills || [],
      required_languages: c.required_languages || [],
      latitude: c.latitude == null ? "" : String(c.latitude),
      longitude: c.longitude == null ? "" : String(c.longitude),
      geofence_radius_m: c.geofence_radius_m == null ? "" : String(c.geofence_radius_m),
      medicaid_member_id: c.medicaid_member_id || "",
      payer_id: c.payer_id || "",
    });
    setEditing(c);
    setActiveTab("factsheet");  // existing clients open on the read-only fact sheet
    try {
      const [inv, meds] = await Promise.all([
        api.get(`/clients/${c.id}/family-invites`),
        api.get(`/clients/${c.id}/medications`),
      ]);
      setInvites(inv.data);
      setMedications(meds.data);
    } catch {
      setInvites([]);
      setMedications([]);
    }
  };

  const close = () => {
    setEditing(null);
    setInvites([]);
    setMedications([]);
    // Clear the deep-link param so the same link can be clicked again later
    if (searchParams.get("client")) {
      const next = new URLSearchParams(searchParams);
      next.delete("client");
      setSearchParams(next, { replace: true });
    }
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        billing_rate: form.billing_rate === "" || form.billing_rate == null ? null : Number(form.billing_rate),
        latitude: form.latitude === "" || form.latitude == null ? null : Number(form.latitude),
        longitude: form.longitude === "" || form.longitude == null ? null : Number(form.longitude),
        geofence_radius_m:
          form.geofence_radius_m === "" || form.geofence_radius_m == null ? null : Number(form.geofence_radius_m),
      };
      if (editing === "new") {
        await api.post("/clients", payload);
        toast.success("Client added");
      } else {
        await api.put(`/clients/${editing.id}`, payload);
        toast.success("Client updated");
      }
      close();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Client deleted");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleCaregiver = (id) => {
    setForm((f) => ({
      ...f,
      assigned_caregiver_ids: f.assigned_caregiver_ids.includes(id)
        ? f.assigned_caregiver_ids.filter((x) => x !== id)
        : [...f.assigned_caregiver_ids, id],
    }));
  };

  const toggleDay = (d) => {
    setForm((f) => ({
      ...f,
      scheduled_days: f.scheduled_days.includes(d)
        ? f.scheduled_days.filter((x) => x !== d)
        : [...f.scheduled_days, d],
    }));
  };

  const isExistingClient = editing && editing !== "new";

  return (
    <div className="space-y-6" data-testid="admin-clients-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">People</div>
          <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">Clients</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setVoiceIntakeOpen(true)} className="btn-secondary"
            data-testid="voice-intake-button">
            <Sparkles className="h-4 w-4" /> Voice intake
          </button>
          <button onClick={startNew} className="btn-primary" data-testid="new-client-button">
            <Plus className="h-4 w-4" /> Add client
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-clinical-subtext">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : clients.length === 0 ? (
        <div className="card-surface p-10 text-center text-clinical-subtext">
          No clients yet. Add your first client.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((c) => (
            <ClientCard
              key={c.id}
              client={c}
              caregivers={caregivers}
              onEdit={startEdit}
              onDelete={remove}
            />
          ))}
        </div>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
          onClick={close}
          data-testid="client-modal"
        >
          <div
            className="bg-white rounded-2xl border border-clinical-border w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 bg-white border-b border-clinical-border">
              <div className="flex items-center justify-between px-5 py-3">
                <h3 className="font-heading text-lg font-medium">
                  {editing === "new" ? "Add client" : editing.name || "Client"}
                </h3>
                <button type="button" onClick={close} className="btn-ghost p-1.5" data-testid="modal-close">
                  <X className="h-4 w-4" />
                </button>
              </div>
              {editing !== "new" && (
                <div className="flex gap-1 px-5 pb-0 overflow-x-auto" role="tablist" aria-label="Client view">
                  {[
                    { v: "factsheet", l: "Fact Sheet" },
                    { v: "edit", l: "Edit" },
                    { v: "strengths", l: "Strengths & Needs" },
                    { v: "risk", l: "Risk" },
                    { v: "info", l: "Client Info" },
                    { v: "consents", l: "Consents" },
                  ].map((t) => (
                    <button
                      key={t.v}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === t.v}
                      onClick={() => setActiveTab(t.v)}
                      className={`shrink-0 px-3 py-2 text-sm font-medium border-b-2 transition ${
                        activeTab === t.v
                          ? "border-clinical-primary text-clinical-primary"
                          : "border-transparent text-clinical-subtext hover:text-clinical-text"
                      }`}
                      data-testid={`client-tab-${t.v}`}
                    >
                      {t.l}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* --- Fact Sheet tab (read-only) --- */}
            {editing !== "new" && activeTab === "factsheet" && (
              <div className="p-5">
                <ClientFactSheet
                  client={{ ...editing, ...form, id: editing.id }}
                  caregiverLookup={Object.fromEntries(caregivers.map((c) => [c.id, c]))}
                />
              </div>
            )}
            {editing !== "new" && activeTab === "strengths" && (
              <div className="p-5">
                <ClientStrengthsNeedsTab clientId={editing.id} />
              </div>
            )}
            {editing !== "new" && activeTab === "risk" && (
              <div className="p-5">
                <ClientRiskAssessmentTab clientId={editing.id} />
              </div>
            )}
            {editing !== "new" && activeTab === "info" && (
              <div className="p-5">
                <ClientInfoTab clientId={editing.id} />
              </div>
            )}
            {editing !== "new" && activeTab === "consents" && (
              <div className="p-5">
                <ClientConsentsTab clientId={editing.id} />
              </div>
            )}
            {(editing === "new" || activeTab === "edit") && (
            <div className="p-5 space-y-4">
              <div>
                <label className="label-eyebrow mb-1.5 block">Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input-clinical"
                  data-testid="client-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-eyebrow mb-1.5 block">Address</label>
                  <input
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="input-clinical"
                    data-testid="client-address-input"
                  />
                </div>
                <div>
                  <label className="label-eyebrow mb-1.5 block">DOB</label>
                  <input
                    type="date"
                    value={form.dob}
                    onChange={(e) => setForm({ ...form, dob: e.target.value })}
                    className="input-clinical"
                  />
                </div>
              </div>

              <ClinicalFields form={form} setForm={setForm} />

              <SkillsSection form={form} setForm={setForm} />
              <GeofenceSection form={form} setForm={setForm} />

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="label-eyebrow">Care notes</label>
                  <button
                    type="button"
                    onClick={() => setVoiceCarePlanOpen(true)}
                    className="inline-flex items-center gap-1 rounded-lg bg-purple-100 px-2 py-1 text-[11px] font-semibold text-purple-700 hover:bg-purple-200"
                    data-testid="client-voice-care-plan-btn"
                  >
                    <Mic size={11} /> Voice care plan
                  </button>
                </div>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  className="input-clinical resize-y"
                />
              </div>
              <div>
                <label className="label-eyebrow mb-1.5 block">Scheduled days</label>
                <div className="flex flex-wrap gap-1.5">
                  {DAYS.map((d) => {
                    const on = form.scheduled_days.includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => toggleDay(d)}
                        data-testid={`day-${d}`}
                        className={`rounded-lg px-3 py-1.5 text-xs font-medium border transition-colors ${
                          on
                            ? "bg-clinical-primary text-white border-clinical-primary"
                            : "bg-white text-clinical-subtext border-clinical-border hover:border-clinical-primary/40"
                        }`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="label-eyebrow mb-1.5 block">Assigned caregivers</label>
                <div className="space-y-1.5">
                  {caregivers.map((cg) => {
                    const on = form.assigned_caregiver_ids.includes(cg.id);
                    return (
                      <button
                        key={cg.id}
                        type="button"
                        onClick={() => toggleCaregiver(cg.id)}
                        data-testid={`assign-${cg.id}`}
                        className={`w-full flex items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors ${
                          on
                            ? "border-clinical-primary bg-clinical-primary/5"
                            : "border-clinical-border bg-white hover:border-clinical-primary/30"
                        }`}
                      >
                        <span>{cg.name}</span>
                        <span className="text-xs text-clinical-subtext">{on ? "Assigned" : "Assign"}</span>
                      </button>
                    );
                  })}
                  {caregivers.length === 0 && (
                    <div className="text-sm text-clinical-subtext">No caregivers in the system yet.</div>
                  )}
                </div>
              </div>

              <MedsLockToggle form={form} setForm={setForm} />
              <QuickContactsSection form={form} setForm={setForm} />

              {isExistingClient && (
                <ClientFamilyInvites
                  clientId={editing.id}
                  invites={invites}
                  setInvites={setInvites}
                />
              )}
              {isExistingClient && (
                <ClientMedications
                  clientId={editing.id}
                  medications={medications}
                  setMedications={setMedications}
                />
              )}
            </div>
            )}
            {/* Save bar — only shown in edit mode (or new) */}
            {(editing === "new" || activeTab === "edit") && (
              <div className="sticky bottom-0 bg-white flex items-center justify-end gap-2 px-5 py-4 border-t border-clinical-border">
                <button type="button" onClick={close} className="btn-secondary" data-testid="modal-cancel">Cancel</button>
                <button type="button" onClick={save} disabled={saving} className="btn-primary" data-testid="modal-save">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <AdminVoiceIntakeModal
        open={voiceIntakeOpen}
        onClose={() => setVoiceIntakeOpen(false)}
        onCreated={async (cid) => {
          await load();
          // Open the freshly-created client modal via deep-link
          setSearchParams({ client: cid });
        }}
      />

      <AdminVoiceFillModal
        open={voiceCarePlanOpen}
        onClose={() => setVoiceCarePlanOpen(false)}
        endpoint="/ai/voice-fill/care-plan"
        title="Voice care plan"
        hint='e.g. "Goals: maintain weight above 110, prevent falls. Services: personal care 3 times a week 30 minutes, medication reminders daily 15 minutes. No dairy. Family rule: no visitors after 8pm."'
        examples={[
          "Goals: maintain weight above 110, prevent falls. Services: personal care 3 times a week 30 minutes, medication reminders daily 15 minutes. No dairy.",
          "Services: bathing twice a week 45 minutes, meal prep daily 30 minutes, light housekeeping weekly.",
          "Goal is hospice comfort. Special instructions no loud TV, classical music only. Call son Mark for any emergency.",
        ]}
        limitMs={150 * 1000}
        filenamePrefix="careplan"
        testIdPrefix="client-care-plan-voice"
        renderPreview={(r) => (
          <div className="space-y-2 text-sm">
            {(r.goals || []).length > 0 && (
              <div>
                <div className="label-eyebrow">Goals</div>
                <ul className="list-disc pl-5 text-clinical-text">
                  {r.goals.map((g, i) => (<li key={i}>{g}</li>))}
                </ul>
              </div>
            )}
            {(r.services || []).length > 0 && (
              <div>
                <div className="label-eyebrow">Services</div>
                <ul className="list-disc pl-5 text-clinical-text text-xs">
                  {r.services.map((s, i) => (
                    <li key={i}>
                      {s.service}
                      {s.frequency && ` · ${s.frequency}`}
                      {s.duration_min != null && ` · ${s.duration_min} min`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(r.special_instructions || []).length > 0 && (
              <div>
                <div className="label-eyebrow">Special instructions</div>
                <ul className="list-disc pl-5 text-clinical-text text-xs">
                  {r.special_instructions.map((s, i) => (<li key={i}>{s}</li>))}
                </ul>
              </div>
            )}
            {r.emergency_protocol && (
              <div>
                <div className="label-eyebrow">Emergency protocol</div>
                <p className="text-clinical-text text-xs">{r.emergency_protocol}</p>
              </div>
            )}
            {r.review_in_days != null && (
              <div className="text-xs text-clinical-subtext">
                Review in {r.review_in_days} days
              </div>
            )}
          </div>
        )}
        onConfirm={(r) => {
          const block = [
            "📋 Care plan (voice-drafted)",
            (r.goals || []).length ? "Goals:\n" + r.goals.map((g) => `• ${g}`).join("\n") : "",
            (r.services || []).length ? "Services:\n" + r.services.map((s) => `• ${s.service}${s.frequency ? " — " + s.frequency : ""}${s.duration_min != null ? " (" + s.duration_min + " min)" : ""}`).join("\n") : "",
            (r.special_instructions || []).length ? "Special instructions:\n" + r.special_instructions.map((s) => `• ${s}`).join("\n") : "",
            r.emergency_protocol ? `Emergency: ${r.emergency_protocol}` : "",
            r.review_in_days != null ? `Review in ${r.review_in_days} days` : "",
          ].filter(Boolean).join("\n\n");
          setForm({
            ...form,
            notes: form.notes ? `${form.notes}\n\n${block}` : block,
          });
          toast.success("Care plan added to notes");
        }}
        confirmLabel="Add to care notes"
      />
    </div>
  );
}

// --- Inline section components (small enough to keep co-located) ---

function SkillsSection({ form, setForm }) {
  return (
    <div className="rounded-lg border border-clinical-border bg-clinical-bg/50 p-3 space-y-2.5" data-testid="client-skills-section">
      <div>
        <div className="label-eyebrow">Skill requirements (optional)</div>
        <p className="text-[11px] text-clinical-subtext mt-0.5">
          Used to rank caregivers when assigning shifts.
        </p>
      </div>
      <div>
        <label className="label-eyebrow mb-1 block text-[10px]">Required skills (caregiver MUST have all)</label>
        <TagInput
          value={form.required_skills || []}
          onChange={(v) => setForm({ ...form, required_skills: v })}
          suggestions={["dementia-care","hoyer-lift","wound-care","cpr","first-aid","g-tube","hospice"]}
          placeholder="e.g. dementia-care, hoyer-lift"
          tagTone="required"
          testid="client-required-skills"
        />
      </div>
      <div>
        <label className="label-eyebrow mb-1 block text-[10px]">Preferred skills (bonus, not required)</label>
        <TagInput
          value={form.preferred_skills || []}
          onChange={(v) => setForm({ ...form, preferred_skills: v })}
          suggestions={["alzheimers","pediatric","spanish","sign-language","cooking"]}
          placeholder="e.g. wound-care, cooking"
          tagTone="preferred"
          testid="client-preferred-skills"
        />
      </div>
      <div>
        <label className="label-eyebrow mb-1 block text-[10px]">Required language(s)</label>
        <TagInput
          value={form.required_languages || []}
          onChange={(v) => setForm({ ...form, required_languages: v })}
          suggestions={["en","es","fr","ar","pt","tl","vi","zh","hi"]}
          placeholder="en, es…"
          testid="client-required-langs"
        />
      </div>
    </div>
  );
}

function GeofenceSection({ form, setForm }) {
  const mapsHref = form.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(form.address)}`
    : "https://www.google.com/maps";
  return (
    <div className="rounded-lg border border-clinical-border bg-clinical-bg/50 p-3 space-y-2.5" data-testid="client-geofence-section">
      <div>
        <div className="label-eyebrow">EVV / Geofence (optional)</div>
        <p className="text-[11px] text-clinical-subtext mt-0.5">
          Caregivers must clock in within this radius. Lookup at{" "}
          <a href={mapsHref} target="_blank" rel="noreferrer" className="text-clinical-primary hover:underline">
            Google Maps
          </a>{" "}
          → right-click pin → copy lat,lng.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <label className="label-eyebrow mb-1 block text-[10px]">Latitude</label>
          <input
            type="number" step="any"
            value={form.latitude}
            onChange={(e) => setForm({ ...form, latitude: e.target.value })}
            className="input-clinical" placeholder="40.7128"
            data-testid="client-lat-input"
          />
        </div>
        <div>
          <label className="label-eyebrow mb-1 block text-[10px]">Longitude</label>
          <input
            type="number" step="any"
            value={form.longitude}
            onChange={(e) => setForm({ ...form, longitude: e.target.value })}
            className="input-clinical" placeholder="-74.0060"
            data-testid="client-lng-input"
          />
        </div>
        <div>
          <label className="label-eyebrow mb-1 block text-[10px]">Radius (m)</label>
          <input
            type="number" min={50} max={2000} step={10}
            value={form.geofence_radius_m}
            onChange={(e) => setForm({ ...form, geofence_radius_m: e.target.value })}
            className="input-clinical" placeholder="(default)"
            data-testid="client-radius-input"
          />
        </div>
      </div>
    </div>
  );
}

function MedsLockToggle({ form, setForm }) {
  return (
    <div className="rounded-lg border border-clinical-border bg-clinical-bg px-3 py-3">
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={!!form.meds_locked}
          onChange={(e) => setForm({ ...form, meds_locked: e.target.checked })}
          className="mt-0.5 accent-clinical-primary"
          data-testid="meds-locked-toggle"
        />
        <span>
          <span className="text-sm font-medium text-clinical-text">Lock medications</span>
          <span className="block text-xs text-clinical-subtext">
            When locked, caregivers can still log doses but cannot add, edit, or
            remove medications for this client.
          </span>
        </span>
      </label>
    </div>
  );
}

function QuickContactsSection({ form, setForm }) {
  return (
    <div className="border-t border-clinical-border pt-4">
      <div className="label-eyebrow mb-2">Quick contacts (tap-to-call)</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Labeled label="Doctor name">
          <input value={form.doctor_name} onChange={(e) => setForm({ ...form, doctor_name: e.target.value })}
            placeholder="Dr. Smith" className="input-clinical" data-testid="doctor-name-input" />
        </Labeled>
        <Labeled label="Doctor phone">
          <input type="tel" value={form.doctor_phone} onChange={(e) => setForm({ ...form, doctor_phone: e.target.value })}
            placeholder="555-123-4567" className="input-clinical" data-testid="doctor-phone-input" />
        </Labeled>
        <Labeled label="Family contact name">
          <input value={form.family_contact_name} onChange={(e) => setForm({ ...form, family_contact_name: e.target.value })}
            placeholder="Lisa (daughter)" className="input-clinical" data-testid="family-contact-name-input" />
        </Labeled>
        <Labeled label="Family phone">
          <input type="tel" value={form.family_contact_phone} onChange={(e) => setForm({ ...form, family_contact_phone: e.target.value })}
            placeholder="555-987-6543" className="input-clinical" data-testid="family-contact-phone-input" />
        </Labeled>
      </div>
      <div className="mt-3">
        <Labeled label="Emergency note (shown to caregiver)">
          <textarea value={form.emergency_note} onChange={(e) => setForm({ ...form, emergency_note: e.target.value })}
            rows={2} placeholder="e.g. Allergic to penicillin. Call daughter first."
            className="input-clinical resize-y" data-testid="emergency-note-input" />
        </Labeled>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 pt-3 border-t border-clinical-border">
        <div className="sm:col-span-1">
          <Labeled label="Billing rate ($/hour)">
            <input type="number" step="0.01" value={form.billing_rate}
              onChange={(e) => setForm({ ...form, billing_rate: e.target.value })}
              placeholder="e.g. 35" className="input-clinical" data-testid="billing-rate-input" />
          </Labeled>
        </div>
        <div className="sm:col-span-2">
          <Labeled label="Billing notes (internal)">
            <input value={form.billing_notes} onChange={(e) => setForm({ ...form, billing_notes: e.target.value })}
              placeholder="e.g. Net 14, family pays via ACH" className="input-clinical"
              data-testid="billing-notes-input" />
          </Labeled>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 pt-3 border-t border-clinical-border">
        <Labeled label="Medicaid Member ID (for EVV)">
          <input value={form.medicaid_member_id}
            onChange={(e) => setForm({ ...form, medicaid_member_id: e.target.value })}
            placeholder="e.g. AA12345B" className="input-clinical"
            data-testid="client-medicaid-id-input" />
        </Labeled>
        <Labeled label="Payer ID (overrides agency default)">
          <input value={form.payer_id}
            onChange={(e) => setForm({ ...form, payer_id: e.target.value })}
            placeholder="e.g. NY-MMIS" className="input-clinical"
            data-testid="client-payer-id-input" />
        </Labeled>
      </div>
    </div>
  );
}

function Labeled({ label, children }) {
  return (
    <div>
      <label className="text-xs text-clinical-subtext block mb-1">{label}</label>
      {children}
    </div>
  );
}
